<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

interface MosaicItem {
  id: string | number
  title: string
  url: string
  content_type: string
  date_published: string
  feed_image?: any
  avatar_data?: any
  page?: any
}

interface Props {
  featured_item?: MosaicItem | null
  grid_items?: MosaicItem[]
  uuid?: string
}

const props = defineProps<Props>()

const scrollContainer = ref<HTMLElement | null>(null)
const activeIndex = ref(0)
const navigationDots = ref<HTMLButtonElement[]>([])
let rafId: number | null = null
const root = ref<HTMLElement | null>(null)

onBeforeMount(() => {
  root.value = (document.querySelector(`[data-uuid="${props.uuid}"]`) as HTMLElement) ?? null;
  if (!root.value) return
  delete root.value.dataset['json']
});

onMounted(() => {
  const container = root.value.querySelector('[data-scroll-container]') as HTMLElement
  const dots = Array.from(root.value.querySelectorAll('[data-nav-dot]')) as HTMLButtonElement[]

  if (container && dots.length > 0) {
    scrollContainer.value = container
    navigationDots.value = dots

    container.addEventListener('scroll', handleScroll, { passive: true })
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        scrollToIndex(index)
      })
    })

    // Initialize first dot as active
    updateActiveDot(0)
  } else {
    console.warn('MosaicFeedGrid: Could not find scroll container or navigation dots', {
      container: !!container,
      dotsCount: dots.length
    })
  }
})

onUnmounted(() => {
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', handleScroll)
  }
  if (rafId !== null) {
    cancelAnimationFrame(rafId)
  }
})

const handleScroll = () => {
  // Use requestAnimationFrame for instant, smooth updates synced with browser repaint
  if (rafId !== null) cancelAnimationFrame(rafId)

  rafId = requestAnimationFrame(() => {
    if (!scrollContainer.value) return

    // Calculate which item is most visible (closest to center)
    const container = scrollContainer.value
    const containerRect = container.getBoundingClientRect()
    const containerCenter = containerRect.left + containerRect.width / 2

    const items = Array.from(container.querySelectorAll('[data-scroll-item]'))
    let closestIndex = 0
    let closestDistance = Infinity

    items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect()
      const itemCenter = itemRect.left + itemRect.width / 2
      const distance = Math.abs(containerCenter - itemCenter)

      if (distance < closestDistance) {
        closestDistance = distance
        closestIndex = index
      }
    })

    if (closestIndex !== activeIndex.value) {
      activeIndex.value = closestIndex
      updateActiveDot(closestIndex)
    }

    rafId = null
  })
}

const scrollToIndex = (index: number) => {
  if (!scrollContainer.value) return

  const items = Array.from(scrollContainer.value.querySelectorAll('[data-scroll-item]'))
  const targetItem = items[index] as HTMLElement

  if (targetItem) {
    // Scroll to center the item
    targetItem.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    })
    activeIndex.value = index
    updateActiveDot(index)
  }
}

const updateActiveDot = (index: number) => {
  navigationDots.value.forEach((dot, i) => {
    if (i === index) {
      dot.classList.add('w-16')
      dot.classList.remove('w-12')
      dot.firstElementChild.classList.add('bg-red-600')
      dot.firstElementChild.classList.remove('bg-gray-100')
      dot.setAttribute('aria-current', 'true')
    } else {
      dot.blur() // Remove focus from non-active dots
      dot.classList.add('w-12')
      dot.classList.remove('w-16')
      dot.firstElementChild.classList.remove('bg-red-600')
      dot.firstElementChild.classList.add('bg-gray-100')
      dot.removeAttribute('aria-current')
    }
  })
}
</script>
